import {
  type MutationFunction,
  type QueryClient,
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useState } from 'react';
import { type StaticDecode, Type } from 'typebox';
import * as TypeCompiler from 'typebox/compile';
import _ from 'underscore';
import * as CustomersUrl from 'auto-converted/lib/CustomersUrl';
import * as DashboardUrl from 'auto-converted/lib/DashboardUrl';
import { createQueue } from 'auto-converted/lib/UpdateQueue';
import type {
  ReferenceCustomer,
  ReferenceCustomerSearchResults,
} from 'schema/reference_customer';
import { ReadonlyArray, ReadonlyObject } from 'schema/schema';
import { type LogEntry, useLogUpdatedCVs } from 'stores/ActivityLogs';
import { UsageUser } from 'stores/ProjectFieldOverview';
import { useSaveFailed } from 'stores/SaveFailed';
import type { StoreMutation } from 'stores/types';
import { FetchError, http, httpChecked, queryClient } from 'util/fetch';
import { useDebouncedInfiniteQuery } from 'util/query';
import { navigateTo, redirectTo } from 'util/routes';

const queries = {
  base: () => ['customers'] as const,
  customers: () => [...queries.base(), 'customers'] as const,
  fetch: (id: string) =>
    queryOptions({
      queryKey: [...queries.customers(), id],
      queryFn: ({ signal }) =>
        http.get<ReferenceCustomer>(`/api/v2/company/cv/customers/${id}`, {
          signal,
        }),
    }),
  names: () => [...queries.base(), 'names'] as const,
  name: (name: string) =>
    infiniteQueryOptions({
      queryKey: [...queries.names(), name] as const,
      queryFn: async ({ signal, pageParam, client }) => {
        const url = new URL(
          '/api/v2/company/cv/customers',
          window.location.origin,
        );

        if (name) {
          url.searchParams.set('customer_name', name);
        }
        url.searchParams.set('size', '100');
        url.searchParams.set('offset', pageParam.toString());

        const res = await http.get<ReferenceCustomerSearchResults>(url, {
          signal,
        });

        for (const customer of res.customers) {
          client.setQueryData(queries.fetch(customer._id).queryKey, customer);
        }

        return res;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const numberOfResults = allPages.flatMap(
          (page) => page.customers,
        ).length;

        if (numberOfResults < lastPage.total) {
          return numberOfResults;
        }

        return undefined;
      },
    }),
};

export function useCustomer(id: string, enabled = true) {
  const { data } = useQuery({
    ...queries.fetch(id),
    staleTime: 60_000,
    enabled,
  });

  return data;
}

export async function prefetchCustomer(id: string) {
  await queryClient.prefetchQuery({ ...queries.fetch(id), staleTime: 60_000 });
}

export async function invalidateCustomer(id: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queries.names() }),
    queryClient.invalidateQueries(queries.fetch(id)),
  ]);
}

export function useCustomers(searchTerm: string) {
  const [forbidden, setForbidden] = useState(false);

  const {
    data,
    hasNextPage = false,
    fetchNextPage,
    isPending,
  } = useDebouncedInfiniteQuery(
    (searchTerm) =>
      infiniteQueryOptions({
        ...queries.name(searchTerm),
        retry: (failureCount, error) => {
          // External users trigger a forbidden response from the API
          if (error instanceof FetchError && error.status === 403) {
            setForbidden(true);
            return false;
          }

          return failureCount < 3;
        },
        select: (data) => {
          const customers = data.pages.flatMap((page) => page.customers);

          return {
            customers,
            total: data.pages.at(-1)?.total ?? customers.length,
          };
        },
        enabled: !forbidden,
        refetchOnWindowFocus: !forbidden,
      }),
    searchTerm,
  ).query;

  return {
    data,
    hasNextPage,
    fetchNextPage: () => fetchNextPage({ cancelRefetch: false }),
    isPending,
  };
}

export async function prefetchCustomers() {
  await queryClient.prefetchInfiniteQuery({
    ...queries.name(''),
    staleTime: Infinity,
  });
}

interface MergeCustomersVariables {
  customers: readonly ReferenceCustomer[];
  selected_for_merge_hash: any;
}

export type MergeCustomersMutation = StoreMutation<
  {
    updated_customer: ReferenceCustomer;
    other_customer_ids: readonly string[];
  },
  MergeCustomersVariables
>;

export function useMergeCustomers() {
  const { mutate } = useMutation({
    mutationFn: async ({
      customers,
      selected_for_merge_hash,
    }: MergeCustomersVariables) => {
      const selected_customer_id =
        _.chain(selected_for_merge_hash.image).values().first().value() ||
        _.first(customers)?._id;
      const customer_ids = customers.map((customer) => customer._id);
      const other_customer_ids = _.without(customer_ids, selected_customer_id);
      const url = `/api/v2/company/cv/customers/${selected_customer_id}/merge`;
      const json = {
        merge_with: other_customer_ids,
        selected_for_merge_hash,
      };
      const updated_customer = await http.put<ReferenceCustomer>(url, {
        body: json,
      });

      return { updated_customer, other_customer_ids };
    },
    onSuccess: (
      { updated_customer, other_customer_ids },
      _vars,
      _onMutateResult,
      { client },
    ) => {
      client.setQueryData(
        queries.fetch(updated_customer._id).queryKey,
        updated_customer,
      );

      for (const customer_id of other_customer_ids) {
        client.removeQueries(queries.fetch(customer_id));
      }
    },
    onError: (_error, { customers }, _onMutateResult, { client }) =>
      Promise.all(
        customers.map((customer) =>
          client.invalidateQueries(queries.fetch(customer._id)),
        ),
      ),
    onSettled: (_data, _error, _vars, _onMutateResult, { client }) =>
      client.invalidateQueries({ queryKey: queries.names() }),
  });

  return mutate;
}

export function useDeleteCustomer() {
  const { mutate } = useMutation({
    mutationFn: (customer_id: string) => {
      const url = `/api/v2/company/cv/customers/${customer_id}`;
      return http.delete(url);
    },
    onSuccess: async (_data, customer_id, _onMutateResult, { client }) => {
      const route = DashboardUrl.anchored_url('customers', false);

      if (window.location.pathname === '/dashboard') {
        await navigateTo(route);

        // remove after navigation so it's not queried again immediately
        client.removeQueries(queries.fetch(customer_id));
      } else {
        redirectTo(`/dashboard#${route}`);
      }
    },
    onError: (_error, customer_id, _onMutateResult, { client }) =>
      client.invalidateQueries(queries.fetch(customer_id)),
    onSettled: (_data, _error, _vars, _onMutateResult, { client }) =>
      client.invalidateQueries({ queryKey: queries.names() }),
  });

  return mutate;
}

interface CreateCustomerVariables {
  name: string;
  language_code: string;
}

export type CreateCustomerMutation = StoreMutation<
  ReferenceCustomer,
  CreateCustomerVariables
>;

export function useCreateCustomer() {
  const { mutate } = useMutation({
    mutationFn: ({ name, language_code }: CreateCustomerVariables) => {
      const url = '/api/v2/company/cv/customers';
      const body = {
        company_customer: { customer_name: { [language_code]: name } },
      };
      return http.post<ReferenceCustomer>(url, { body });
    },
    onSuccess: async (
      customer,
      { language_code },
      _onMutateResult,
      { client },
    ) => {
      client.setQueryData(queries.fetch(customer.id).queryKey, customer);

      redirectTo(CustomersUrl.edit_path(customer.id, language_code));

      await client.invalidateQueries({ queryKey: queries.names() });
    },
  });

  return mutate;
}

const queue = createQueue();

interface UpdateVariables {
  customer_id: string;
}

function useUpdateCustomer<Variables extends UpdateVariables>(
  mutationFn: MutationFunction<ReferenceCustomer, Variables>,
  {
    invalidateDescriptions,
    updateAfterMutation,
  }: { invalidateDescriptions?: boolean; updateAfterMutation?: boolean } = {},
) {
  const saveFailed = useSaveFailed();

  const { mutate, isPending } = useMutation({
    mutationFn: (vars: Variables, context) => {
      const { customer_id, ...update_hash } = vars;

      return queue.add(() => mutationFn(vars, context), {
        debugInfo: { customer_id, update_hash },
      });
    },
    onSuccess: updateAfterMutation
      ? (data, _vars, _onMutateResult, { client }) => {
          client.setQueryData(queries.fetch(data._id).queryKey, data);
        }
      : undefined,
    onError: (error, { customer_id }, _onMutateResult, { client }) => {
      saveFailed(`company_customer::${customer_id}`, error);

      return client.invalidateQueries(queries.fetch(customer_id));
    },
    onSettled: (_data, _error, { customer_id }, _onMutateResult, { client }) =>
      Promise.all([
        client.invalidateQueries({ queryKey: queries.names() }),
        invalidateDescriptions
          ? invalidateCustomerDescriptions(customer_id)
          : null,
      ]),
  });

  return { updateCustomer: mutate, isPending };
}

interface DeleteCustomTagVariables extends UpdateVariables {
  custom_tag_id: string;
}

export function useDeleteCustomTag() {
  const { updateCustomer, isPending } =
    useUpdateCustomer<DeleteCustomTagVariables>(
      ({ customer_id, custom_tag_id }) => {
        const url = `/api/v2/company/cv/customers/${customer_id}/custom_tags/${custom_tag_id}`;
        return http.delete(url);
      },
      { updateAfterMutation: true },
    );
  return { deleteCustomTag: updateCustomer, isPending };
}

interface AddCustomTagVariables extends UpdateVariables {
  custom_tag_category_id: string;
  custom_tag_id: string;
}

export function useAddCustomTag() {
  const { updateCustomer, isPending } =
    useUpdateCustomer<AddCustomTagVariables>(
      ({ customer_id, custom_tag_category_id, custom_tag_id }) => {
        const url = `/api/v2/company/cv/customers/${customer_id}/custom_tags`;
        const json = {
          custom_tag_category_id,
          custom_tag_id,
        };
        return http.post(url, { body: json });
      },
      { updateAfterMutation: true },
    );
  return { addCustomTag: updateCustomer, isPending };
}

type UpdateCustomerVariables = Partial<
  Omit<ReferenceCustomer, 'id' | '_id' | 'company_id' | 'version'>
>;

async function updateBaseCustomer(
  queryClient: QueryClient,
  customerId: string,
  update_hash: Partial<UpdateCustomerVariables>,
) {
  const url = `/api/v2/company/cv/customers/${customerId}`;

  const customer = queryClient.getQueryData(queries.fetch(customerId).queryKey);
  const new_update_hash = customer
    ? { ...update_hash, version: customer.version }
    : update_hash;

  const updated_customer = await http.put<ReferenceCustomer>(url, {
    body: { company_customer: new_update_hash },
  });

  // updating cache data here rather than in `onSuccess` to ensure the cache is
  // updated before the next queued `updateCustomer` runs.
  queryClient.setQueryData(
    queries.fetch(updated_customer._id).queryKey,
    updated_customer,
  );

  return updated_customer;
}

interface UpdateDescriptionVariables extends UpdateVariables {
  new_description: string;
  language_code: string;
}

export function useReplaceDescription() {
  const { updateCustomer, isPending } =
    useUpdateCustomer<UpdateDescriptionVariables>(
      ({ customer_id, new_description, language_code }, { client }) =>
        updateBaseCustomer(client, customer_id, {
          customer_description_to_cvs: {
            [language_code]: new_description,
          },
        }),
      { invalidateDescriptions: true },
    );
  return {
    replaceDescription: updateCustomer,
    isPending,
  };
}

interface ChangeIndustryVariables extends UpdateVariables {
  industry_id: string;
}

export type ChangeIndustryMutation = StoreMutation<
  ReferenceCustomer,
  ChangeIndustryVariables
>;

export function useChangeIndustry() {
  const { updateCustomer } = useUpdateCustomer<ChangeIndustryVariables>(
    ({ customer_id, industry_id }, { client }) => {
      // @ts-expect-error - the API expects `industry_id`, but it's really `masterdata_industry_id`
      const update: UpdateCustomerVariables = { industry_id };
      return updateBaseCustomer(client, customer_id, update);
    },
  );
  return updateCustomer;
}

interface UpdateFieldVariables
  extends UpdateVariables, UpdateCustomerVariables {}

export type UpdateFieldMutation = StoreMutation<
  ReferenceCustomer,
  UpdateFieldVariables
>;

export function useUpdateField() {
  const { updateCustomer } = useUpdateCustomer<UpdateFieldVariables>(
    ({ customer_id, ...update_hash }, { client }) =>
      updateBaseCustomer(client, customer_id, update_hash),
  );
  return updateCustomer;
}

const CustomerDescription = ReadonlyObject({
  customer_description: Type.String(),
  count: Type.Integer(),
  users: ReadonlyArray(UsageUser),
});

const customerDescriptionChecker = TypeCompiler.Compile(
  ReadonlyArray(CustomerDescription),
);

type CustomerDescription = StaticDecode<typeof CustomerDescription>;
type CustomerDescriptions = readonly CustomerDescription[];

const customerDescriptionQueries = {
  base: ['customer-descriptions'] as const,
  fetch: (customer_id: string, language_code: string) =>
    queryOptions({
      queryKey: [
        ...customerDescriptionQueries.base,
        { customer_id },
        { language_code },
      ] as const,
      queryFn: ({ signal }) =>
        httpChecked.get(
          `/api/v2/company/cv/customers/unapproved/project_experiences/customer_description?language_code=${language_code}&customer_id=${customer_id}`,
          customerDescriptionChecker,
          { signal },
        ),
    }),
};

export function useCustomerDescriptions(
  customer_id: string,
  language_code: string,
): CustomerDescriptions | undefined {
  const { data } = useQuery({
    ...customerDescriptionQueries.fetch(customer_id, language_code),
    staleTime: 60_000,
  });

  return data;
}

export function usePollingCustomerDescriptions(
  customer_id: string,
  language_code: string,
  enabled: boolean,
) {
  useQuery({
    ...customerDescriptionQueries.fetch(customer_id, language_code),
    refetchInterval: 1_000,
    enabled,
  });
}

interface ChangeCustomerDescription {
  original_value: string;
  new_value: string;
  customer_id: string;
  language_code: string;
  limit_to_user_ids: readonly string[];
}

export function useChangeCustomerDescription() {
  const logUpdatedCVs = useLogUpdatedCVs();

  return useMutation({
    mutationFn: ({ language_code, ...body }: ChangeCustomerDescription) =>
      http.put<{ updated_cvs: readonly LogEntry[] }>(
        `/api/v2/update-terms/${language_code}/project_experiences/customer_description`,
        { body },
      ),
    onSuccess: ({ updated_cvs }) => logUpdatedCVs(updated_cvs),
    onSettled: (
      _data,
      _error,
      { customer_id, language_code },
      _onMutateResult,
      { client },
    ) =>
      Promise.all([
        client.invalidateQueries(
          customerDescriptionQueries.fetch(customer_id, language_code),
        ),
        invalidateCustomer(customer_id),
      ]),
  });
}

export async function prefetchCustomerDescriptions(
  customer_id: string,
  language_code: string,
) {
  await queryClient.prefetchQuery({
    ...customerDescriptionQueries.fetch(customer_id, language_code),
    staleTime: Infinity,
  });
}

async function invalidateCustomerDescriptions(customer_id: string) {
  // language will be popped off
  const { queryKey } = customerDescriptionQueries.fetch(customer_id, 'fake');
  const allCustomerKey = queryKey.slice(0, -1);

  await queryClient.invalidateQueries({ queryKey: allCustomerKey });
}

export async function invalidateAllCustomerDescriptions() {
  await queryClient.invalidateQueries({
    queryKey: customerDescriptionQueries.base,
  });
}
